import { useMemo, useEffect, FC } from "react";
import { useParams } from "react-router-dom";
import { useCardFiltering } from "@/hooks/useCardFiltering";
import { useLayoutFiltering } from "@/hooks/useLayoutFiltering";
import { useInboxPanel } from "@/hooks/useInboxPanel";
import { InboxPanel } from "@/components/Common/InboxPanel";
import { DashboardHeader } from "@/components/Page/Header/DashboardHeader";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
} from "@/components/Base/Table";
import { ListViewCard } from "./ListViewCard";
import { useHeaderContext } from "@/hooks/useHeaderContext";
import { usePersistentColumns } from "./hooks/usePersistentColumns";
import { ResizableTableHeader } from "./components/ResizableTableHeader";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { TerminologyOptions } from "@/types/v2";
import { usePersistentSorting } from "./hooks/usePersistentSorting";
import { usePersistentGrouping } from "./hooks/usePersistentGrouping";
import { useListViewGrouping } from "./hooks/useListViewGrouping";
import { GroupBySelector } from "./components/GroupBySelector";
import { GroupContainer } from "./components/GroupContainer";
import { ListViewControlsMenu } from "./components/ListViewControlsMenu";
import { ShowAllRepairOrdersToggle } from "./components/ShowAllRepairOrdersToggle";
import { useShowAllRepairOrders } from "./hooks/useShowAllRepairOrders";
import { useEmployees } from "@/hooks/shop/useEmployees";
import { useStore } from "@/stores/useStore";
import { useEnhancedColumns } from "./hooks/useEnhancedColumns";
import { useMoveToDialog } from "./hooks/useMoveToDialog";
import { MoveToDialog } from "./components/MoveToDialog";

export const DashboardListView: FC = () => {
  const { shopId } = useParams();

  // Initialize show all repair orders toggle
  const { showAllRepairOrders, setShowAllRepairOrders } =
    useShowAllRepairOrders({
      shopId: shopId,
      defaultValue: false,
    });

  // Initialize layout filtering with bypass capability
  const { applyLayoutFilter, layoutVisibleContainers, isLayoutSelected } =
    useLayoutFiltering({ bypassLayout: showAllRepairOrders });

  // Use card filtering with layout filter integration
  const { filteredCards } = useCardFiltering({
    applyLayoutFilter: applyLayoutFilter,
  });
  const { terminolize } = useTerminologyReplacer();
  const { isOpen: inboxPanelOpen, panelWidth } = useInboxPanel();
  const employees = useEmployees();
  const { shop, tags } = useStore();
  const shopContainers = useMemo(
    () => shop?.containers ?? [],
    [shop?.containers]
  );

  // Filter containers to only include those visible in the current layout
  const visibleContainers = useMemo(() => {
    if (
      !isLayoutSelected ||
      layoutVisibleContainers.length === 0 ||
      showAllRepairOrders
    ) {
      // If no layout is selected, or if showing all repair orders, show all containers
      return shopContainers.map((c) => ({
        id: c.id,
        name: c.displayName ?? c.id,
      }));
    }

    // Only show containers that are in the current layout
    return shopContainers
      .filter((c) => layoutVisibleContainers.includes(c.id))
      .map((c) => ({
        id: c.id,
        name: c.displayName ?? c.id,
      }));
  }, [
    shopContainers,
    layoutVisibleContainers,
    isLayoutSelected,
    showAllRepairOrders,
  ]);

  const { setHeaderContent } = useHeaderContext();

  // Initialize multi-level sorting with persistence
  const {
    sortingState,
    addSort,
    removeSort,
    clearSort,
    reorderSort,
    sortCards,
    sortInfo,
  } = usePersistentSorting({
    shopId: shopId,
    shop: shop,
    config: {
      maxLevels: 3,
      persistUserPreferences: true,
    },
  });

  // Initialize grouping with persistence
  const { groupingConfig, setGroupBy, toggleGroupCollapse } =
    usePersistentGrouping({
      shopId: shopId,
    });

  // Initialize column management with persistence
  const {
    columns: baseColumns,
    visibleColumns: baseVisibleColumns,
    hiddenColumnIds,
    columnOrder,
    updateColumnVisibility,
    updateColumnWidth,
    reorderColumns,
    resetToDefaults,
  } = usePersistentColumns({
    shopId: shopId,
  });

  // Enhance columns with dynamic field labels
  const columns = useEnhancedColumns(baseColumns);
  const visibleColumns = useEnhancedColumns(baseVisibleColumns);

  // Initialize Move To Dialog
  const {
    isOpen: moveDialogOpen,
    selectedCard,
    openDialog: openMoveDialog,
    closeDialog: closeMoveDialog,
    handleMoveSuccess,
  } = useMoveToDialog({
    onMoveSuccess: (card, newContainer) => {
      console.log(
        `Successfully moved card ${card.id} to container ${newContainer}`
      );
      // The card data will be updated through Firestore real-time updates
      // So we don't need to manually update any state here
    },
  });

  // Apply grouping to filtered and sorted cards
  const { groups, ungroupedCards, totalCards, totalGroups } =
    useListViewGrouping({
      cards: filteredCards,
      config: groupingConfig,
      employees: employees,
      containers: visibleContainers,
      sortCards: sortCards,
    });

  useEffect(() => {
    setHeaderContent(<DashboardHeader />);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  // Performance monitoring
  useEffect(() => {
    const startTime = performance.now();

    // Monitor large dataset performance
    if (filteredCards.length > 500) {
      console.warn(
        `Dashboard List View: Rendering ${filteredCards.length} cards. Consider implementing virtual scrolling for better performance.`
      );
    }

    // Log render time after the component has rendered
    const timeoutId = setTimeout(() => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;

      if (renderTime > 200) {
        console.warn(
          `Dashboard List View: Slow render detected (${renderTime.toFixed(2)}ms) with ${filteredCards.length} cards`
        );
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [filteredCards.length]);

  const dynamicWidth = useMemo(() => {
    const padding = 0; // Total horizontal padding (8px each side)
    const baseWidth = `calc(100vw - ${padding}px)`; // Default width with some padding

    if (inboxPanelOpen) {
      return `calc(100vw - ${panelWidth}px - ${padding}px)`;
    }
    return baseWidth;
  }, [inboxPanelOpen, panelWidth]);

  return (
    <>
      <div
        className="flex h-screen overflow-hidden"
        style={{ width: dynamicWidth }}
      >
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Performance Info and Controls */}
          <div className="border-b">
            {/* Stats and Control Bar */}
            <div className="flex items-center justify-between px-4 py-2">
              <div className="text-sm text-white/70">
                Showing {totalCards}{" "}
                {terminolize(
                  totalCards === 1 ? "repair order" : "repair orders",
                  TerminologyOptions.repairOrder,
                  totalCards === 1 ? "singular" : "plural"
                )}
                {groupingConfig.groupBy !== "none" && totalGroups > 0 && (
                  <span className="ml-2 text-white/60">
                    in {totalGroups} {totalGroups === 1 ? "group" : "groups"}
                  </span>
                )}
                {sortInfo.hasSorting && (
                  <span className="ml-2 text-white/60">
                    (Sorted by {sortInfo.sortCount}{" "}
                    {sortInfo.sortCount === 1 ? "criteria" : "criteria"})
                  </span>
                )}
                {totalCards > 1000 && (
                  <span className="ml-2 font-medium text-yellow-600">
                    (Large dataset - consider filtering)
                  </span>
                )}
              </div>

              {/* Control Menu */}
              <div className="flex items-center gap-2">
                <ShowAllRepairOrdersToggle
                  showAllRepairOrders={showAllRepairOrders}
                  onToggleChange={setShowAllRepairOrders}
                  isLayoutSelected={isLayoutSelected}
                />
                <GroupBySelector
                  groupBy={groupingConfig.groupBy}
                  onGroupByChange={setGroupBy}
                />
                <ListViewControlsMenu
                  sortCriteria={sortingState.criteria}
                  onReorderSort={reorderSort}
                  onRemoveSort={removeSort}
                  onClearSort={clearSort}
                  hasSorting={sortInfo.hasSorting}
                  columns={columns}
                  hiddenColumnIds={hiddenColumnIds}
                  columnOrder={columnOrder}
                  onUpdateColumnVisibility={updateColumnVisibility}
                  onReorderColumns={reorderColumns}
                  onResetColumnsToDefaults={resetToDefaults}
                />
              </div>
            </div>
          </div>

          {/* List Container */}
          <div className="m-1 flex-1 overflow-auto">
            {groupingConfig.groupBy === "none" ? (
              /* Ungrouped View - Traditional Table */
              <div className="rounded-lg border border-slate-700 [&_thead]:border-slate-700 [&_tr]:border-slate-700">
                <Table style={{ tableLayout: "fixed", width: "100%" }}>
                  <TableHeader>
                    <TableRow>
                      {visibleColumns.map((column) => (
                        <ResizableTableHeader
                          key={column.id}
                          column={column}
                          sortCriteria={sortingState.criteria}
                          onSort={addSort}
                          onWidthChange={updateColumnWidth}
                        />
                      ))}
                      {/* Actions column header */}
                      <ResizableTableHeader
                        key="actions"
                        column={{
                          id: "actions",
                          field: "actions",
                          label: "Actions",
                          width: 120,
                          minWidth: 120,
                          sortable: false,
                          filterable: false,
                          visible: true,
                          fixed: true,
                          align: "center" as const,
                        }}
                        sortCriteria={sortingState.criteria}
                        onSort={addSort}
                        onWidthChange={updateColumnWidth}
                      />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ungroupedCards.map((card) => (
                      <ListViewCard
                        key={card.id}
                        card={card}
                        columns={visibleColumns}
                        employees={employees}
                        shop={shop}
                        tags={tags}
                        disabled={false}
                        onMoveToClick={openMoveDialog}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              /* Grouped View - Group Containers */
              <div className="space-y-4">
                {groups.map((group) => (
                  <GroupContainer
                    key={group.id}
                    group={group}
                    columns={visibleColumns}
                    showStatistics={groupingConfig.showStatistics}
                    showCounts={groupingConfig.showCounts}
                    sortCriteria={sortingState.criteria}
                    onToggleCollapse={toggleGroupCollapse}
                    onSort={addSort}
                    onColumnWidthChange={updateColumnWidth}
                    employees={employees}
                    shop={shop}
                    tags={tags}
                    disabled={false}
                    onMoveToClick={openMoveDialog}
                  />
                ))}
              </div>
            )}

            {/* Empty State */}
            {totalCards === 0 && (
              <div className="flex h-32 items-center justify-center text-muted-foreground">
                No cards found matching the current filters.
              </div>
            )}
          </div>
        </div>
      </div>
      <InboxPanel />

      {/* Move To Dialog */}
      {selectedCard && (
        <MoveToDialog
          isOpen={moveDialogOpen}
          onClose={closeMoveDialog}
          card={selectedCard}
          onMoveSuccess={handleMoveSuccess}
        />
      )}
    </>
  );
};
